import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, merge, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ErrorModalComponent } from '../../core/modals/error-modal/error-modal.component';
import { AuthenticationService } from '../../core/services/authentication.service';
import { LogCategory, LogType, LogWidget, LoggerService } from '../../core/services/logger.service';
import { ProfileService } from '../../core/services/profile.service';
import { SolarWindsService } from '../../services/solar-winds.service';
import { TableauService } from '../../services/tableau.service';
import { LoadingWrapper } from '../../shared/loadingwrapper';
import { IWidgetComponent } from '../../shared/widget/i-widget-component';
import { Widget } from '../../shared/widget/widget';

@Component({
    selector: 'my-support-requests',
    templateUrl: './my-support-requests.component.html',
    styleUrls: ['./my-support-requests.component.scss'],
    standalone: false
})
export class MySupportRequestsComponent implements OnInit, OnDestroy, AfterViewInit, IWidgetComponent {
    public myRequests;
    public tableauDashboards;

    public currentTab: string = 'open';
    public myTableauRequests: any;
    public data$: Observable<any>;
    public emptyObservable$: Observable<any> = of('');
    public tab$: Observable<string>;

    private dataStore: any =
    {
        tableau: {
            count: -1,
            requests: [],
            tableauAttestationUrl: ''
        },
        support : {
            pendingCount: -1,
            completedCount: -1,
            classRecordingsCount: -1,
            reportUrl: '',
            requestUrl: '',
            supportRequests: []
        }
    };
    public data: Widget;
    public isExpanded: boolean[] = [false];
    public isCancelling: boolean[] = [];
    public cancelRequest: Observable<any>;
    public cancelError$ = new Subject<boolean>();

    constructor(
        private logger: LoggerService,
        public requestService: SolarWindsService,
        public tableauService: TableauService,
        private modalService: NgbModal,
        public profileService: ProfileService,
        private changeDetector: ChangeDetectorRef,
        public auth: AuthenticationService,
        private route: ActivatedRoute,
    ) {
        this.log("constructor", null, LogCategory.Constructor);
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Widget, LogWidget.MySupportRequests, category);
    }

    public ngOnInit() {
        this.log("ngOnInit", null, LogCategory.Init);
        this.myTableauRequests = this.auth.hasFeature('Tableau') ? new LoadingWrapper(this.tableauService.myTableauRequests$) : new LoadingWrapper(this.emptyObservable$);
        this.tableauDashboards = this.auth.hasFeature('Tableau') ? new LoadingWrapper(this.tableauService.tableauDashboards$) : new LoadingWrapper(this.emptyObservable$);
        this.myRequests = new LoadingWrapper(this.requestService.myRequests$);
                
        // We need to catch any errors so we can handle it differently in the UI
        // We still want the solarwinds or tableau tabs to load if the other one is not working
        let myRequests$ = this.myRequests.data$.pipe(
            catchError(error => of({ error: 'solarwinds' }))
        );

        let myTableauRequests$ = this.myTableauRequests.data$.pipe(                      
            catchError(error => of({ error: 'tableau' }))            
        )

        // merge observables so we can get data as soon as each completes
        // the solarwinds api is usually slower than tableau
        this.data$ = merge(myTableauRequests$, myRequests$).pipe(            
            map((data: any) => {
                if (data.tableauRequests) {
                    this.dataStore.tableau = {
                        count: data.tableauRequests.length,
                        requests: data.tableauRequests,
                        tableauAttestationUrl : data.tableauAttestationUrl
                    }                    
                }
                if (data.classRecordings) {
                    this.dataStore.support = {
                        pendingCount: data.pending.length,
                        completedCount: data.completed.length,
                        classRecordingsCount: data.classRecordings.length,
                        reportUrl: data.reportUrl,
                        requestUrl: data.requestUrl,
                        supportRequests: data                        
                    }                   
                }
                if (data.error) {
                    if (data.error == 'solarwinds') 
                        this.dataStore.support.error = 'solarwinds error';                                                                 
                        
                    if (data.error == 'tableau')
                        this.dataStore.tableau.error = 'tableau error';
                }
                return this.dataStore
            })            
        )

        this.tab$ = this.route.queryParamMap.pipe(
            map((params: ParamMap) => params.get('t')),
            tap((val) => {
                val = val?.toLowerCase();
                // Switch below is to reset the default opening tab if the user does not have permissions
                switch (val) {
                    case "tableau":
                        if (!this.auth.hasFeature('Tableau'))
                            val = 'open';
                        break;
                    case "course recordings":
                        if (!(this.auth.audience.includes('|Faculty|') || this.auth.audience.includes('|PhD|')))
                            val = 'open';
                        break;
                    case null:
                    case undefined:
                        val = 'open'
                        break;
                    default:
                        break;
                }
                this.currentTab = val;
            })
        );
    }

    public ngOnDestroy() {
        this.log("ngOnDestroy", "", LogCategory.Destroy);
    }

    public cancel(requestId) {
        this.isCancelling[requestId] = true;
        this.cancelRequest = this.requestService.updateRequestStatusToCancelled(requestId).pipe(
            catchError((error) => {
                this.cancelError$.next(true);
                this.modalService.open(ErrorModalComponent);
                return of()
            }),
            finalize(() => { this.refresh(); })
        );
    }

    public refresh() {
        this.requestService.refresh();
        this.tableauService.refresh();
    }

    public ngAfterViewInit() {
        // See https://blog.angularindepth.com/everything-you-need-to-know-about-the-expressionchangedafterithasbeencheckederror-error-e3fd9ce7dbb4
        // Added after error handling template added to component.
        this.changeDetector.detectChanges();
    }
}


